import React, {useState, useEffect} from 'react';
import {defaultErrorHandler} from '../../context/context';
import redirectUtils from '../../utils/redirect-utils';
import billingV1Client from '../../infrastructure/acerill/billing-v1-client';
import subscriptionService from '../../services/subscription-service';
import CenteredSpinner from '../../libs/react-components-admin/spinners/centered-spinner';
import {ActionResult} from '../../libs/react-components-admin/buttons/action-button';
import {displayYesNoModal} from '../../libs/react-components-admin/modal/yes-no-modal';
import BillingPlan from '../../libs/react-components-admin/billing/billing-plan';
import Section from '../../libs/react-components-admin/section/section';
import BillingFrequency from '../../libs/react-components-admin/billing/billing-frequency';

const featuresByPlanCode = {
  FREE: [
    'Maximum 3 submissions per day',
    'Integrates with Dropbox, reCATPCHA, Google Sheets & Zapier',
    'Storage: 30 days (submissions and files)',
    'Max. 1Mb per file',
  ],
  BASIC: [
    'Unlimited forms and submissions',
    'Integrates with Dropbox, reCATPCHA, Google Sheets & Zapier',
    'Storage: 90 days (submissions and files)',
    'Max. 10Mb per file',
    'Access to support team',
  ],
  PREMIUM: [
    'Unlimited forms and submissions',
    'Integrates with Dropbox, reCATPCHA, Google Sheets & Zapier',
    'Storage: 2 years (submissions and files)',
    'Max. 100Mb per file',
    'Access to support team',
  ],
  UNLIMITED: [
    'Unlimited forms and submissions',
    'Integrates with Dropbox, reCATPCHA, Google Sheets & Zapier',
    'Storage: 10 years (submissions and files)',
    'Max. 2Gb per file (except Dropbox which has a limit of 150Mb)',
    'Access to support team',
  ],
};

const BillingSubscription = () => {

  const [currentSubscription, setCurrentSubscription] = useState();
  const [selectedBillingFrequency, setSelectedBillingFrequency] = useState();
  const [plans, setPlans] = useState();

  useEffect(() => {
    subscriptionService
      .findCurrentSubscription()
      .then(subscription => {
        setCurrentSubscription(subscription);
        setSelectedBillingFrequency(subscription.frequency || 'monthly');
      })
      .catch(defaultErrorHandler);
  }, []);

  useEffect(() => {
    if (!selectedBillingFrequency) {
      return;
    }
    setPlans(null);
    billingV1Client.findPlans(selectedBillingFrequency)
      .then(plans => {
        setPlans(plans.map(plan => ({
          ...plan, features: featuresByPlanCode[plan.code],
        })));
      })
      .catch(defaultErrorHandler);
  }, [selectedBillingFrequency]);


  const isDowngrade = (fromPlan, toPlan) => {
    if (fromPlan === toPlan) {
      return false;
    } else if (toPlan === 'FREE') {
      return true;
    } else if (toPlan === 'BASIC') {
      return fromPlan !== 'FREE';
    } else if (toPlan === 'PREMIUM') {
      return fromPlan !== 'FREE' && fromPlan !== 'BASIC';
    } else {
      return false;
    }
  };

  const subscribe = code => {
    const updateSubscriptionPromise = isDowngrade(currentSubscription.planCode, code) ?
      displayYesNoModal(
        <div className="text-center">Some data might be deleted (e.g. submissions, files uploaded), are you sure you
          wish to downgrade your subscription?</div>,
        defaultErrorHandler
      ) :
      Promise.resolve(true);
    return updateSubscriptionPromise
      .then(yesOrNo => {
        if (!yesOrNo) {
          return ActionResult.aborted;
        }
        return subscriptionService
          .updateSubscription('CUSTOM_FORMS', code, selectedBillingFrequency)
          .then(subscription => {
            if (subscription.confirmationUrl) {
              redirectUtils.redirectTo(subscription.confirmationUrl);
              setCurrentSubscription(null);
              setPlans(null);
            } else {
              setCurrentSubscription(subscription);
            }
          });
      });
  };

  return <Section title="Pricing plans" noMargin={true}>
    <div className="row justify-content-center mb-4">
      <div className="col-auto">
        {selectedBillingFrequency &&
        <BillingFrequency initialValue={selectedBillingFrequency}
                          onChange={v => setSelectedBillingFrequency(v)}/>
        }
      </div>
    </div>
    <div className="row mb-4">
      {!selectedBillingFrequency || !currentSubscription || !plans ?
        <CenteredSpinner/> :
        <>
          {plans.map(plan =>
            <BillingPlan key={plan.code}
                         name={plan.name} code={plan.code}
                         price={<>
                           <span className="h3 me-1">{'$' + plan.priceAsString}</span>
                          {selectedBillingFrequency === 'yearly' ? '/year' : '/month'}
                         </>}
                         isCurrentPlan={currentSubscription.planCode === plan.code &&
                         (currentSubscription.planCode === 'FREE' ||
                          currentSubscription.frequency === selectedBillingFrequency)
                         }
                         features={plan.features}
                         onSelectPlanClicked={code => subscribe(code)}
                         errorHandler={defaultErrorHandler}/>
          )}
        </>
      }
    </div>
  </Section>;
};

export default BillingSubscription;
